import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useAvatarUpload } from '../../hooks/useAvatarUpload';
import { usePinLockContext } from '../../contexts/PinLockContext';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { PlanTab } from './components/PlanTab';
import { ProfileCompletenessCard, calculateProfileCompleteness } from './components/ProfileCompletenessCard';
import { ClientPaymentsTab } from './components/ClientPaymentsTab';
import { supabase } from '../../lib/supabase';
import { NotificationPreferencesTab } from './components/NotificationPreferencesTab';
import { SessionDevicesTab } from './components/SessionDevicesTab';
import { logMyEvent } from '../../lib/audit';
import { useInstallPWA } from '../../hooks/useInstallPWA';
import { IOSInstructionsModal } from '../../components/feature/InstallPWABanner';
import { useOnboardingTour, TOUR_STEPS } from '../../contexts/OnboardingTourContext';

const TABS = [
  { id: 'dados', label: 'Dados Pessoais', icon: 'ri-user-line' },
  { id: 'endereco', label: 'Endereço', icon: 'ri-map-pin-line' },
  { id: 'seguranca', label: 'Segurança', icon: 'ri-shield-line' },
  { id: 'dispositivos', label: 'Dispositivos', icon: 'ri-device-line' },
  { id: 'plano', label: 'Meu Plano', icon: 'ri-vip-crown-line' },
  { id: 'pagamentos', label: 'Pagamentos', icon: 'ri-receipt-line' },
  { id: 'notificacoes', label: 'Notificações', icon: 'ri-notification-3-line' },
  { id: 'ajuda', label: 'Ajuda & Tutorial', icon: 'ri-compass-discover-line' },
];

const CURRENCIES = [
  { value: 'BRL', label: 'Real Brasileiro (R$)' },
  { value: 'USD', label: 'Dólar Americano ($)' },
  { value: 'EUR', label: 'Euro (€)' },
];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: 'Ativa', color: 'text-emerald-700 bg-emerald-50' },
  blocked: { label: 'Bloqueada', color: 'text-red-700 bg-red-50' },
  inactive: { label: 'Inativa', color: 'text-gray-600 bg-gray-100' },
};

const TIMEOUT_OPTIONS = [
  { value: 5, label: '5 minutos' },
  { value: 10, label: '10 minutos' },
  { value: 15, label: '15 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 60, label: '1 hora' },
];

const BRAZIL_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO',
];

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtDatetime = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const maskCpf = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const maskCep = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return d.replace(/(\d{5})(\d)/, '$1-$2');
};

const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

type PinStep = 'idle' | 'setup-enter' | 'setup-confirm' | 'change-old' | 'change-new' | 'change-confirm';

interface FormData {
  name: string;
  first_name: string;
  last_name: string;
  display_name: string;
  full_name: string;
  phone: string;
  cpf: string;
  currency: string;
  timezone: string;
}

interface AddressData {
  cep: string;
  street: string;
  street_number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
}

// ─── PinDigitInput ────────────────────────────────────────────────────────────
// Re-mounts when `stepKey` changes so autoFocus re-triggers each step.
const PinDigitInput = ({
  value,
  onChange,
  stepKey,
}: {
  value: string;
  onChange: (v: string) => void;
  stepKey: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the hidden input whenever stepKey changes (new step)
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [stepKey]);

  return (
    <div
      className="flex gap-3 justify-center my-3 cursor-text outline-none"
      onClick={() => inputRef.current?.focus()}
      role="group"
      aria-label="Entrada de PIN"
    >
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-bold transition-all select-none ${
            i < value.length
              ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
              : i === value.length
              ? 'border-emerald-300 bg-white ring-2 ring-emerald-100'
              : 'border-gray-200 bg-gray-50 text-gray-300'
          }`}
        >
          {i < value.length ? '•' : '–'}
        </div>
      ))}
      {/* Single source of truth: hidden input captures ALL input events.
          No onKeyDown on the container to avoid double-firing. */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        autoComplete="off"
        value={value}
        onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: '50%',
          opacity: 0,
          width: '1px',
          height: '1px',
          fontSize: '16px',
          border: 'none',
          outline: 'none',
        }}
        aria-hidden="true"
      />
    </div>
  );
};

// ─── FaqItem ──────────────────────────────────────────────────────────────────
const FaqItem = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <span className="text-sm font-600 text-gray-800">{question}</span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-gray-400 shrink-0`} />
      </button>
      {open && (
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-500 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default function PerfilPage() {
  const { user, profile, refreshProfile, signOut } = useAuth();
  const { isPaid } = useSubscription();
  const install = useInstallPWA();
  const { restartTour } = useOnboardingTour();

  const [activeTab, setActiveTab] = useState('dados');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [addrSaved, setAddrSaved] = useState(false);
  const [addrError, setAddrError] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [pwError, setPwError] = useState('');
  const [signingOut, setSigningOut] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // ─── PIN state ──────────────────────────────────────────────────────────────
  // Use shared context so lock() affects the overlay in AppLayout
  const pinLock = usePinLockContext();

  // Local UI state for PIN setup/change flow (independent of context)
  const [pinStep, setPinStep] = useState<PinStep>('idle');
  const [pinInput, setPinInput] = useState('');
  const [pinFirstEntry, setPinFirstEntry] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pinOld, setPinOld] = useState('');
  const [pinMsg, setPinMsg] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '', first_name: '', last_name: '', display_name: '',
    full_name: '', phone: '', cpf: '', currency: 'BRL', timezone: '',
  });

  const [addressData, setAddressData] = useState<AddressData>({
    cep: '', street: '', street_number: '', complement: '',
    neighborhood: '', city: '', state: '', country: 'Brasil',
  });

  const [pwData, setPwData] = useState({ newPw: '', confirm: '' });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatar = useAvatarUpload(user?.id, refreshProfile);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name ?? '',
        first_name: profile.first_name ?? '',
        last_name: profile.last_name ?? '',
        display_name: profile.display_name ?? '',
        full_name: profile.full_name ?? '',
        phone: profile.phone ?? '',
        cpf: profile.cpf ?? '',
        currency: profile.currency ?? 'BRL',
        timezone: profile.timezone ?? '',
      });
      setAddressData({
        cep: profile.cep ?? '',
        street: profile.street ?? '',
        street_number: profile.street_number ?? '',
        complement: profile.complement ?? '',
        neighborhood: profile.neighborhood ?? '',
        city: profile.city ?? '',
        state: profile.state ?? '',
        country: profile.country ?? 'Brasil',
      });
    }
  }, [profile]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && TABS.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  }, []);

  const displayName = profile?.display_name || profile?.name || profile?.full_name || user?.email || 'Usuário';
  const initials = displayName.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase() || 'U';
  const authProvider = profile?.auth_provider ?? 'email';

  const googleAvatarUrl = authProvider === 'google'
    ? (user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture ?? null)
    : null;
  const avatarUrl = profile?.avatar_url ?? googleAvatarUrl;
  const isUsingGoogleAvatar = !profile?.avatar_url && !!googleAvatarUrl;

  const accountStatus = profile?.account_status ?? 'active';
  const statusConfig = STATUS_LABELS[accountStatus] ?? STATUS_LABELS['active'];

  const completeness = profile ? calculateProfileCompleteness(profile) : null;

  const handleCepBlur = async () => {
    const raw = addressData.cep.replace(/\D/g, '');
    if (raw.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setAddressData(prev => ({
          ...prev,
          street: data.logradouro || prev.street,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.localidade || prev.city,
          state: data.uf || prev.state,
          country: 'Brasil',
        }));
      }
    } catch (_) {
      // silent fail
    } finally {
      setCepLoading(false);
    }
  };

  const handleSave = async () => {
    setSaveError('');
    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    const fullNameDerived = [firstName, lastName].filter(Boolean).join(' ') || formData.full_name;

    const { error } = await supabase.from('profiles').update({
      name: formData.name,
      first_name: firstName,
      last_name: lastName,
      display_name: formData.display_name,
      full_name: fullNameDerived,
      phone: formData.phone,
      cpf: formData.cpf.replace(/\D/g, ''),
      currency: formData.currency,
      timezone: formData.timezone,
      updated_at: new Date().toISOString(),
    }).eq('id', user?.id);
    if (error) { setSaveError('Erro ao salvar. Tente novamente.'); return; }
    logMyEvent('update', { section: 'personal_data' });
    await refreshProfile();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleAddressSave = async () => {
    setAddrError('');
    const { error } = await supabase.from('profiles').update({
      cep: addressData.cep.replace(/\D/g, ''),
      street: addressData.street,
      street_number: addressData.street_number,
      complement: addressData.complement,
      neighborhood: addressData.neighborhood,
      city: addressData.city,
      state: addressData.state,
      country: addressData.country,
      updated_at: new Date().toISOString(),
    }).eq('id', user?.id);
    if (error) { setAddrError('Erro ao salvar endereço. Tente novamente.'); return; }
    logMyEvent('update', { section: 'address' });
    await refreshProfile();
    setAddrSaved(true);
    setTimeout(() => setAddrSaved(false), 2500);
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (pwData.newPw.length < 6) { setPwError('A nova senha deve ter ao menos 6 caracteres.'); return; }
    if (pwData.newPw !== pwData.confirm) { setPwError('As senhas não coincidem.'); return; }
    const { error } = await supabase.auth.updateUser({ password: pwData.newPw });
    if (error) { setPwError('Erro ao alterar senha. Tente novamente.'); return; }
    logMyEvent('settings_change', { type: 'password_changed' });
    setPwSaved(true);
    setPwData({ newPw: '', confirm: '' });
    setTimeout(() => setPwSaved(false), 3000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return;
    avatar.uploadAvatar(file);
    e.target.value = '';
  };

  const resetPin = () => {
    setPinStep('idle');
    setPinInput('');
    setPinFirstEntry('');
    setPinConfirm('');
    setPinOld('');
    setPinMsg('');
  };

  const handlePinActivate = async () => {
    if (pinStep === 'setup-enter') {
      if (pinInput.length !== 4) { setPinMsg('Digite exatamente 4 dígitos.'); return; }
      // Save first entry and move to confirm step
      setPinFirstEntry(pinInput);
      setPinInput('');          // Reset so user types fresh in confirm step
      setPinStep('setup-confirm');
      setPinMsg('');
      return;
    }
    if (pinStep === 'setup-confirm') {
      if (pinInput.length !== 4) { setPinMsg('Digite exatamente 4 dígitos.'); return; }
      if (pinInput !== pinFirstEntry) {
        setPinMsg('Os PINs não coincidem. Tente novamente.');
        setPinInput('');
        return;
      }
      setPinLoading(true);
      console.log('[PIN] Activating PIN for user', user?.id);
      try {
        await pinLock.activatePin(pinInput);
        console.log('[PIN] Activated successfully');
      } catch (err) {
        console.error('[PIN] Activation error:', err);
        setPinMsg('Erro ao salvar PIN. Tente novamente.');
        setPinLoading(false);
        return;
      }
      setPinLoading(false);
      resetPin();
    }
  };

  const handlePinDeactivate = async () => {
    setPinLoading(true);
    console.log('[PIN] Deactivating PIN');
    try {
      await pinLock.deactivatePin();
      console.log('[PIN] Deactivated successfully');
    } catch (err) {
      console.error('[PIN] Deactivation error:', err);
    }
    setPinLoading(false);
    resetPin();
  };

  const handleChangePin = async () => {
    if (pinStep === 'change-old') {
      if (pinInput.length !== 4) { setPinMsg('Digite o PIN atual.'); return; }
      const ok = await pinLock.verifyPin(pinInput);
      if (!ok) { setPinMsg('PIN atual incorreto.'); setPinInput(''); return; }
      setPinOld(pinInput);
      setPinInput('');
      setPinStep('change-new');
      setPinMsg('');
      return;
    }
    if (pinStep === 'change-new') {
      if (pinInput.length !== 4) { setPinMsg('Digite o novo PIN.'); return; }
      setPinFirstEntry(pinInput);
      setPinInput('');
      setPinStep('change-confirm');
      setPinMsg('');
      return;
    }
    if (pinStep === 'change-confirm') {
      if (pinInput.length !== 4) { setPinMsg('Digite o novo PIN novamente.'); return; }
      if (pinInput !== pinFirstEntry) { setPinMsg('Os PINs não coincidem.'); setPinInput(''); return; }
      setPinLoading(true);
      console.log('[PIN] Changing PIN');
      try {
        const ok = await pinLock.changePin(pinOld, pinInput);
        if (!ok) {
          setPinMsg('Erro ao alterar PIN. Tente novamente.');
          setPinLoading(false);
          return;
        }
        console.log('[PIN] Changed successfully');
      } catch (err) {
        console.error('[PIN] Change error:', err);
        setPinMsg('Erro ao alterar PIN. Tente novamente.');
        setPinLoading(false);
        return;
      }
      setPinLoading(false);
      resetPin();
    }
  };

  const inputCls = "w-full h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 transition-all";
  const disabledInputCls = "w-full h-10 px-3 border border-gray-100 rounded-xl text-sm text-gray-400 bg-gray-50 outline-none cursor-not-allowed";
  const labelCls = "block text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5";

  return (
    <div className="max-w-5xl w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-xl font-700 text-gray-900">Perfil &amp; Configurações</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gerencie seus dados, segurança e plano</p>
      </div>

      {/* Profile completeness banner */}
      {profile && completeness && completeness.pct < 100 && (
        <ProfileCompletenessCard
          profile={profile}
          onComplete={() => setActiveTab('dados')}
        />
      )}

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Sidebar */}
        <div className="lg:w-52 lg:shrink-0 space-y-3">
          {/* Tab nav — horizontal scroll on mobile, vertical on desktop */}
          <div className="bg-white rounded-2xl border border-gray-100 p-2 lg:p-3">
            <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-1 lg:pb-0">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer lg:w-full whitespace-nowrap shrink-0 lg:shrink ${activeTab === tab.id ? 'bg-emerald-50 text-emerald-700 font-600' : 'text-gray-600 hover:bg-gray-50 font-500'}`}
                >
                  <i className={`${tab.icon} text-base`} />
                  <span className="lg:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* User card — hidden on mobile */}
          <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 p-4 text-center">
            <div className="relative w-16 h-16 mx-auto mb-3">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-16 h-16 rounded-2xl object-cover object-top" />
              ) : (
                <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center text-white text-xl font-700">{initials}</div>
              )}
              {authProvider === 'google' && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-gray-200">
                  <span className="text-[10px]">G</span>
                </div>
              )}
            </div>
            <p className="text-sm font-700 text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-400 truncate mt-0.5">{user?.email}</p>
            <span className={`inline-flex items-center gap-1 mt-2 text-xs font-600 px-2.5 py-1 rounded-full ${isPaid ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              <i className={isPaid ? 'ri-vip-crown-fill' : 'ri-user-line'} />
              {isPaid ? 'Premium' : 'Gratuito'}
            </span>

            {/* Completeness mini-pill */}
            {completeness && completeness.pct < 100 && (
              <div className={`mt-2 flex items-center justify-center gap-1.5 text-xs font-600 px-2 py-1 rounded-full ${completeness.pct < 40 ? 'bg-red-50 text-red-600' : completeness.pct < 80 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                <i className="ri-user-settings-line" />
                Perfil {completeness.pct}%
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">

          {/* ===== DADOS PESSOAIS ===== */}
          {activeTab === 'dados' && (
            <div className="space-y-4">
              {/* Avatar */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
                <h3 className="text-sm font-700 text-gray-900 mb-4">Foto de perfil</h3>
                <div className="flex items-center gap-4 sm:gap-5">
                  <div className="relative group shrink-0">
                    <div onClick={() => !avatar.uploading && fileInputRef.current?.click()} className="w-18 h-18 sm:w-20 sm:h-20 rounded-2xl overflow-hidden cursor-pointer relative" style={{ width: '4.5rem', height: '4.5rem' }}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full bg-emerald-500 rounded-2xl flex items-center justify-center text-white text-2xl font-700">{initials}</div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <i className="ri-camera-line text-white text-xl" />
                      </div>
                    </div>
                    {avatar.uploading && (
                      <div className="absolute inset-0 bg-white/80 rounded-2xl flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-emerald-500 text-xl" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5 min-w-0">
                    <p className="text-sm text-gray-700 font-500">
                      {isUsingGoogleAvatar ? 'Foto do Google' : avatarUrl ? 'Foto atual' : 'Nenhuma foto adicionada'}
                    </p>
                    <p className="text-xs text-gray-400">JPG, PNG ou WebP · máx. 5MB</p>
                    {isUsingGoogleAvatar && (
                      <p className="text-xs text-gray-400">Carregada automaticamente da sua conta Google</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-1">
                      <button onClick={() => fileInputRef.current?.click()} disabled={avatar.uploading} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-600 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                        <i className="ri-upload-line text-sm" />{avatarUrl && !isUsingGoogleAvatar ? 'Trocar foto' : 'Adicionar foto própria'}
                      </button>
                      {profile?.avatar_url && (
                        <button onClick={() => avatar.removeAvatar()} disabled={avatar.uploading} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-500 hover:bg-red-100 text-xs font-600 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                          <i className="ri-delete-bin-line text-sm" />Remover
                        </button>
                      )}
                    </div>
                    {avatar.error && <p className="text-xs text-red-500">{avatar.error}</p>}
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFileChange} />
                {authProvider === 'google' && (
                  <div className="mt-4 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl w-fit">
                    <i className="ri-google-fill text-sm text-gray-600" />
                    <span className="text-xs text-gray-600 font-500">Conectado com Google</span>
                  </div>
                )}
              </div>

              {/* Personal info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
                <h3 className="text-sm font-700 text-gray-900 mb-4">Informações pessoais</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Nome</label>
                    <input type="text" value={formData.first_name} onChange={e => setFormData(p => ({ ...p, first_name: e.target.value }))} placeholder="Seu primeiro nome" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Sobrenome</label>
                    <input type="text" value={formData.last_name} onChange={e => setFormData(p => ({ ...p, last_name: e.target.value }))} placeholder="Seu sobrenome" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Como quer ser chamado(a)</label>
                    <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Apelido ou nome preferido" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Nome de exibição</label>
                    <input type="text" value={formData.display_name} onChange={e => setFormData(p => ({ ...p, display_name: e.target.value }))} placeholder="Como aparece no sistema" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>E-mail</label>
                    <input type="text" value={user?.email ?? ''} disabled className={disabledInputCls} />
                    <p className="text-xs text-gray-400 mt-1">O e-mail não pode ser alterado aqui</p>
                  </div>
                  <div>
                    <label className={labelCls}>Telefone</label>
                    <input type="text" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: maskPhone(e.target.value) }))} placeholder="(11) 99999-0000" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>CPF</label>
                    <input type="text" value={maskCpf(formData.cpf)} onChange={e => setFormData(p => ({ ...p, cpf: e.target.value.replace(/\D/g, '') }))} placeholder="000.000.000-00" maxLength={14} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Moeda padrão</label>
                    <select value={formData.currency} onChange={e => setFormData(p => ({ ...p, currency: e.target.value }))} className={inputCls + ' bg-white'}>
                      {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Fuso horário</label>
                    <input type="text" value={formData.timezone} onChange={e => setFormData(p => ({ ...p, timezone: e.target.value }))} placeholder="Ex: America/Sao_Paulo" className={inputCls} />
                  </div>
                </div>

                {saveError && (
                  <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                    <i className="ri-error-warning-line" />{saveError}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-5">
                  <button onClick={handleSave} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 cursor-pointer whitespace-nowrap transition-all ${saved ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}>
                    {saved ? <><i className="ri-check-line" />Salvo!</> : 'Salvar alterações'}
                  </button>
                </div>
              </div>

              {/* Account info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
                <h3 className="text-sm font-700 text-gray-900 mb-4">Informações da conta</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-1">Status da conta</p>
                    <span className={`inline-flex items-center gap-1.5 text-sm font-700 px-2.5 py-1 rounded-lg ${statusConfig.color}`}>
                      <i className="ri-shield-check-line text-xs" />{statusConfig.label}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-1">Forma de acesso</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-600 text-gray-700">
                      <i className={authProvider === 'google' ? 'ri-google-fill text-orange-500' : 'ri-mail-line text-gray-400'} />
                      {authProvider === 'google' ? 'Google' : 'E-mail e senha'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-1">Conta criada em</p>
                    <p className="text-sm font-600 text-gray-800">{fmtDate(profile?.created_at ?? user?.created_at)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-1">Último acesso</p>
                    <p className="text-sm font-600 text-gray-800">{fmtDatetime(profile?.last_seen_at)}</p>
                  </div>
                  {profile?.wizard_completed !== undefined && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-gray-400 mb-1">Configuração inicial</p>
                      <span className={`inline-flex items-center gap-1 text-sm font-600 ${profile.wizard_completed ? 'text-emerald-700' : 'text-amber-600'}`}>
                        <i className={profile.wizard_completed ? 'ri-checkbox-circle-line' : 'ri-time-line'} />
                        {profile.wizard_completed ? 'Concluída' : 'Pendente'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== ENDEREÇO ===== */}
          {activeTab === 'endereco' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-emerald-600 text-base" />
                  </div>
                  <div>
                    <h3 className="text-sm font-700 text-gray-900">Endereço completo</h3>
                    <p className="text-xs text-gray-400">Usado para documentos e cobranças</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>CEP</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={maskCep(addressData.cep)}
                        onChange={e => setAddressData(p => ({ ...p, cep: e.target.value.replace(/\D/g, '') }))}
                        onBlur={handleCepBlur}
                        placeholder="00000-000"
                        maxLength={9}
                        className={inputCls + ' pr-9'}
                      />
                      {cepLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <i className="ri-loader-4-line animate-spin text-gray-400 text-sm" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Preencha o CEP para preenchimento automático</p>
                  </div>
                  <div>
                    <label className={labelCls}>País</label>
                    <input type="text" value={addressData.country} onChange={e => setAddressData(p => ({ ...p, country: e.target.value }))} placeholder="Brasil" className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Rua / Logradouro</label>
                    <input type="text" value={addressData.street} onChange={e => setAddressData(p => ({ ...p, street: e.target.value }))} placeholder="Rua, Avenida, Travessa..." className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Número</label>
                    <input type="text" value={addressData.street_number} onChange={e => setAddressData(p => ({ ...p, street_number: e.target.value }))} placeholder="123" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Complemento</label>
                    <input type="text" value={addressData.complement} onChange={e => setAddressData(p => ({ ...p, complement: e.target.value }))} placeholder="Apto, Bloco, Sala..." className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Bairro</label>
                    <input type="text" value={addressData.neighborhood} onChange={e => setAddressData(p => ({ ...p, neighborhood: e.target.value }))} placeholder="Bairro" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Cidade</label>
                    <input type="text" value={addressData.city} onChange={e => setAddressData(p => ({ ...p, city: e.target.value }))} placeholder="Cidade" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Estado</label>
                    <select value={addressData.state} onChange={e => setAddressData(p => ({ ...p, state: e.target.value }))} className={inputCls + ' bg-white'}>
                      <option value="">Selecione</option>
                      {BRAZIL_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {addrError && (
                  <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                    <i className="ri-error-warning-line" />{addrError}
                  </div>
                )}

                <div className="flex items-center gap-3 mt-5">
                  <button onClick={handleAddressSave} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 cursor-pointer whitespace-nowrap transition-all ${addrSaved ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}>
                    {addrSaved ? <><i className="ri-check-line" />Endereço salvo!</> : 'Salvar endereço'}
                  </button>
                </div>
              </div>

              {/* Address preview */}
              {(addressData.street || addressData.city) && (
                <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
                  <p className="text-xs font-700 text-gray-500 uppercase tracking-wide mb-2">Endereço atual</p>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-map-pin-2-line text-emerald-600 text-sm" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 font-500">
                        {[addressData.street, addressData.street_number, addressData.complement].filter(Boolean).join(', ')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {[addressData.neighborhood, addressData.city, addressData.state].filter(Boolean).join(', ')}
                        {addressData.cep && ` — CEP ${maskCep(addressData.cep)}`}
                      </p>
                      <p className="text-sm text-gray-400">{addressData.country}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== SEGURANÇA ===== */}
          {activeTab === 'seguranca' && (
            <div className="space-y-4">
              {authProvider !== 'google' && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h3 className="text-sm font-700 text-gray-900 mb-1">Alterar senha</h3>
                  <p className="text-xs text-gray-400 mb-4">Use uma senha forte com pelo menos 6 caracteres.</p>
                  <div className="space-y-4 max-w-sm">
                    <div>
                      <label className={labelCls}>Nova senha</label>
                      <input type="password" placeholder="••••••••" value={pwData.newPw} onChange={e => setPwData(p => ({ ...p, newPw: e.target.value }))} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Confirmar nova senha</label>
                      <input type="password" placeholder="••••••••" value={pwData.confirm} onChange={e => setPwData(p => ({ ...p, confirm: e.target.value }))} className={inputCls} />
                    </div>
                  </div>
                  {pwError && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 max-w-sm">
                      <i className="ri-error-warning-line" />{pwError}
                    </div>
                  )}
                  <button onClick={handleChangePassword} className={`mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 cursor-pointer whitespace-nowrap transition-all ${pwSaved ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}>
                    {pwSaved ? <><i className="ri-check-line" />Senha alterada!</> : 'Alterar senha'}
                  </button>
                </div>
              )}

              {authProvider === 'google' && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center shrink-0">
                      <i className="ri-google-fill text-orange-500 text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-700 text-gray-900 mb-1">Conta Google</p>
                      <p className="text-sm text-gray-500 leading-relaxed">Sua conta está vinculada ao Google. Para alterar a senha, acesse as configurações da sua conta Google.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* PIN */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h3 className="text-sm font-700 text-gray-900">PIN de segurança</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Bloqueio rápido da sessão após inatividade. Não substitui sua senha de login.</p>
                  </div>
                  {pinLock.isPinEnabled && (
                    <span className="inline-flex items-center gap-1 text-xs font-600 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full">
                      <i className="ri-shield-check-fill text-xs" />Ativo
                    </span>
                  )}
                </div>

                {!pinLock.isPinEnabled && pinStep === 'idle' && (
                  <button
                    onClick={() => { setPinStep('setup-enter'); setPinInput(''); setPinMsg(''); }}
                    className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-lock-password-line" />Ativar PIN
                  </button>
                )}

                {(pinStep === 'setup-enter' || pinStep === 'setup-confirm') && (
                  <div className="mt-4 max-w-xs">
                    <p className="text-sm font-600 text-gray-700 mb-1">
                      {pinStep === 'setup-enter' ? 'Escolha um PIN de 4 dígitos:' : 'Confirme seu PIN:'}
                    </p>
                    <PinDigitInput
                      key={pinStep}
                      stepKey={pinStep}
                      value={pinInput}
                      onChange={setPinInput}
                    />
                    {pinMsg && <p className="text-xs text-red-500 text-center mb-2">{pinMsg}</p>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={resetPin} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-500 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">
                        Cancelar
                      </button>
                      <button
                        onClick={handlePinActivate}
                        disabled={pinLoading || pinInput.length < 4}
                        className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                      >
                        {pinLoading && <i className="ri-loader-4-line animate-spin" />}
                        {pinStep === 'setup-enter' ? 'Próximo' : 'Salvar PIN'}
                      </button>
                    </div>
                  </div>
                )}

                {pinLock.isPinEnabled && pinStep === 'idle' && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className={labelCls}>Bloquear após</label>
                      <select
                        value={pinLock.timeoutMinutes}
                        onChange={async e => { await pinLock.updateTimeout(Number(e.target.value)); }}
                        className="h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 bg-white"
                      >
                        {TIMEOUT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">de inatividade</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setPinStep('change-old'); setPinInput(''); setPinMsg(''); }}
                        className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                      >
                        <i className="ri-edit-line text-sm" />Alterar PIN
                      </button>
                      <button
                        onClick={handlePinDeactivate}
                        disabled={pinLoading}
                        className="flex items-center gap-1.5 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-500 text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                      >
                        <i className="ri-lock-unlock-line text-sm" />Desativar PIN
                      </button>
                    </div>
                    <button
                      onClick={() => pinLock.lock()}
                      className="flex items-center gap-1.5 px-4 py-2 border border-emerald-200 text-emerald-600 hover:bg-emerald-50 text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-lock-line text-sm" />Bloquear sessão agora
                    </button>
                  </div>
                )}

                {(pinStep === 'change-old' || pinStep === 'change-new' || pinStep === 'change-confirm') && (
                  <div className="mt-4 max-w-xs">
                    <p className="text-sm font-600 text-gray-700 mb-1">
                      {pinStep === 'change-old' ? 'Digite o PIN atual:' : pinStep === 'change-new' ? 'Novo PIN de 4 dígitos:' : 'Confirme o novo PIN:'}
                    </p>
                    <PinDigitInput
                      key={pinStep}
                      stepKey={pinStep}
                      value={pinInput}
                      onChange={setPinInput}
                    />
                    {pinMsg && <p className="text-xs text-red-500 text-center mb-2">{pinMsg}</p>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={resetPin} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-500 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">
                        Cancelar
                      </button>
                      <button
                        onClick={handleChangePin}
                        disabled={pinLoading || pinInput.length < 4}
                        className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                      >
                        {pinLoading && <i className="ri-loader-4-line animate-spin" />}Próximo
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* App / Tela Inicial */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
                    <i className="ri-home-4-line text-emerald-600 text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-700 text-gray-900">Adicionar PandaPro à tela inicial</h3>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                      Instale como app e acesse com um toque direto da tela inicial, sem abrir o navegador.
                    </p>
                  </div>
                </div>

                {install.isMobile ? (
                  <button
                    onClick={install.openInstallFlow}
                    disabled={install.installState === 'installing'}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                  >
                    {install.installState === 'installing' ? (
                      <><i className="ri-loader-4-line animate-spin" />Instalando...</>
                    ) : (
                      <><i className={install.isIOSDevice ? 'ri-share-forward-line' : 'ri-add-circle-line'} />Adicionar ao celular</>
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <i className="ri-information-line text-gray-400 text-sm shrink-0" />
                    <p className="text-xs text-gray-500">
                      Abra o PandaPro no celular para adicionar à tela inicial.
                    </p>
                  </div>
                )}
              </div>

              {/* Session */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="text-sm font-700 text-gray-900 mb-1">Sessão</h3>
                <p className="text-xs text-gray-400 mb-4">Ao sair, você precisará fazer login novamente.</p>
                <button onClick={() => setShowSignOutConfirm(true)} className="flex items-center gap-2 px-5 py-2.5 border-2 border-red-100 text-red-500 hover:bg-red-50 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-all">
                  <i className="ri-logout-box-r-line" />Sair da conta
                </button>
              </div>
            </div>
          )}

          {/* ===== DISPOSITIVOS ===== */}
          {activeTab === 'dispositivos' && (
            <SessionDevicesTab />
          )}

          {/* ===== PLANO ===== */}
          {activeTab === 'plano' && (
            <PlanTab onUpgrade={() => setShowUpgrade(true)} />
          )}

          {/* ===== PAGAMENTOS ===== */}
          {activeTab === 'pagamentos' && (
            <ClientPaymentsTab />
          )}

          {/* ===== NOTIFICAÇÕES ===== */}
          {activeTab === 'notificacoes' && (
            <NotificationPreferencesTab />
          )}

          {/* ===== AJUDA & TUTORIAL ===== */}
          {activeTab === 'ajuda' && (
            <div className="space-y-4">
              {/* Restart tour */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-11 h-11 bg-emerald-50 rounded-2xl flex items-center justify-center shrink-0">
                    <i className="ri-compass-discover-line text-emerald-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-700 text-gray-900">Tour Guiado do Sistema</h3>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                      Revisão interativa dos principais recursos da plataforma. Pode ser feita a qualquer momento, quantas vezes quiser.
                    </p>
                  </div>
                </div>

                {/* Steps preview */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  {TOUR_STEPS.slice(1, 5).map((s) => (
                    <div key={s.id} className="bg-gray-50 rounded-xl p-3 text-center">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className={`${s.icon} text-emerald-600 text-sm`} />
                      </div>
                      <p className="text-[11px] font-600 text-gray-700 leading-tight">{s.title.split('—')[0].trim()}</p>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => restartTour()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-play-circle-line" />
                  Ver tutorial novamente
                </button>
                <p className="text-xs text-gray-400 mt-2">
                  O tour vai te redirecionar para o Dashboard e começar do passo 1.
                </p>
              </div>

              {/* FAQ */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="text-sm font-700 text-gray-900 mb-4">Dúvidas frequentes</h3>
                <div className="space-y-3">
                  {[
                    { q: 'Como registrar uma despesa?', a: 'Acesse "Lançamentos" no menu lateral e clique em "+ Novo lançamento". Preencha os dados e selecione a categoria.' },
                    { q: 'Como criar uma meta financeira?', a: 'Vá em "Metas", clique em "Nova meta", defina o valor alvo e prazo. Registre contribuições à medida que progride.' },
                    { q: 'Como conectar minha conta bancária?', a: 'Em "Contas", clique em "Nova conta" e preencha os dados manualmente. A integração bancária automática está planejada para versões futuras.' },
                    { q: 'Meus dados são seguros?', a: 'Sim! Todos os dados são isolados por usuário com criptografia em repouso e em trânsito. Utilizamos infraestrutura Supabase com RLS habilitado.' },
                  ].map((item, i) => (
                    <FaqItem key={i} question={item.q} answer={item.a} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />

      {/* iOS install instructions (triggered from Settings) */}
      <IOSInstructionsModal open={install.showIOSModal} onClose={install.closeIOSModal} />

      {showSignOutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSignOutConfirm(false)} />
          <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-r-line text-red-500 text-xl" />
            </div>
            <h3 className="text-base font-700 text-gray-900 mb-2">Sair da conta?</h3>
            <p className="text-sm text-gray-500 mb-5">Você precisará fazer login novamente para acessar o sistema.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowSignOutConfirm(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancelar</button>
              <button onClick={async () => { setSigningOut(true); await signOut(); }} disabled={signingOut} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap">
                {signingOut ? 'Saindo...' : 'Confirmar saída'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
